/** The fetch wrapper: CSRF header, claim-header refusal, a view generation, error decoding (ruling 0.4, UX02). */
import {
  CLAIM_HEADERS,
  COMMANDS_PATH,
  CSRF_HEADER,
  PAGE_ROWS_MAX,
  SESSION_PATH,
  VIEWS_PATH,
} from "../contracts/constants.ts";
import { ContractViolation, type Reader } from "../contracts/decode.ts";
import {
  decodeApiError,
  decodeEnvelope,
  decodeSessionView,
  type ApiErrorBody,
  type ReadEnvelope,
  type SessionView,
} from "../contracts/envelope.ts";

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

/** A non-2xx answer: the status, the decoded body (kinds only) and any `Retry-After`. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;
  readonly retryAfterSeconds: number | null;

  constructor(status: number, body: ApiErrorBody | null, retryAfterSeconds: number | null) {
    super(`request refused with status ${String(status)}`);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Query parameters of a projection read. */
export interface ReadParams {
  readonly accountId?: string | null;
  readonly filters?: Readonly<Record<string, string>>;
  readonly pageRows?: number;
  readonly cursor?: string;
  readonly entityId?: string;
}

export interface ClientOptions {
  readonly fetch: FetchLike;
  readonly onUnauthorised?: () => void;
}

/** Refuse a header that claims identity or authority (the server refuses the request too). */
export function assertNoClaimHeaders(headers: Readonly<Record<string, string>>): void {
  for (const name of Object.keys(headers)) {
    if (CLAIM_HEADERS.includes(name.toLowerCase())) {
      throw new ContractViolation(`header ${name}`, "no identity or authority claim");
    }
  }
}

function retryAfter(response: Response): number | null {
  const header = response.headers.get("Retry-After");
  const seconds = header === null ? Number.NaN : Number(header);
  return Number.isSafeInteger(seconds) ? seconds : null;
}

async function failure(response: Response): Promise<ApiRequestError> {
  try {
    return new ApiRequestError(
      response.status,
      decodeApiError(await response.json()),
      retryAfter(response),
    );
  } catch {
    return new ApiRequestError(response.status, null, retryAfter(response));
  }
}

/** Fetches JSON for the views; a new generation aborts in-flight requests and drops late answers. */
export class ApiClient {
  private readonly options: ClientOptions;
  private generationNumber = 0;
  private controller = new AbortController();
  private established: { view: SessionView; token: string } | null = null;

  constructor(options: ClientOptions) {
    this.options = options;
  }

  /** The current view generation (sent on every read and handshake). */
  get generation(): number {
    return this.generationNumber;
  }

  /** Start a new view generation: abort everything in flight and drop late responses (UX02). */
  newGeneration(): number {
    this.controller.abort();
    this.controller = new AbortController();
    this.generationNumber += 1;
    return this.generationNumber;
  }

  /** Forget the session and the CSRF token (logout). */
  forget(): void {
    this.newGeneration();
    this.established = null;
  }

  private async send(
    method: "GET" | "POST",
    path: string,
    body: unknown,
  ): Promise<{ json: unknown; response: Response } | null> {
    const generation = this.generationNumber;
    const headers: Record<string, string> = { Accept: "application/json" };
    if (method === "POST") {
      headers["Content-Type"] = "application/json";
      headers[CSRF_HEADER] = (await this.establish()).token;
    }
    assertNoClaimHeaders(headers);
    try {
      const response = await this.options.fetch(path, {
        method,
        headers,
        credentials: "same-origin",
        cache: "no-store",
        signal: this.controller.signal,
        ...(method === "POST" ? { body: JSON.stringify(body) } : {}),
      });
      if (generation !== this.generationNumber) {
        return null;
      }
      if (!response.ok) {
        const error = await failure(response);
        if (error.status === 401) {
          this.options.onUnauthorised?.();
        }
        throw error;
      }
      const json: unknown = await response.json();
      return generation === this.generationNumber ? { json, response } : null;
    } catch (error) {
      if (generation !== this.generationNumber) {
        return null;
      }
      throw error;
    }
  }

  private async establish(): Promise<{ view: SessionView; token: string }> {
    if (this.established !== null) {
      return this.established;
    }
    const response = await this.options.fetch(SESSION_PATH, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!response.ok) {
      const error = await failure(response);
      if (error.status === 401) {
        this.options.onUnauthorised?.();
      }
      throw error;
    }
    const view = decodeSessionView(await response.json());
    const token = response.headers.get(CSRF_HEADER);
    if (token === null) {
      throw new ContractViolation(`header ${CSRF_HEADER}`, "a CSRF token");
    }
    this.established = { view, token };
    return this.established;
  }

  /** Read the public session view and keep the CSRF token (held in memory only). */
  async session(): Promise<SessionView> {
    return (await this.establish()).view;
  }

  /** GET `path` with `params` and decode the JSON; `null` when the generation moved on. */
  async get<T>(
    path: string,
    params: URLSearchParams,
    decode: (json: unknown) => T,
  ): Promise<T | null> {
    const query = params.toString();
    const answered = await this.send("GET", query === "" ? path : `${path}?${query}`, undefined);
    return answered === null ? null : decode(answered.json);
  }

  /** POST a JSON body (with the CSRF header) and decode the answer; `null` when the generation moved on. */
  async post<T>(path: string, body: unknown, decode: (json: unknown) => T): Promise<T | null> {
    const answered = await this.send("POST", path, body);
    return answered === null ? null : decode(answered.json);
  }

  /** Read a projection as a `ReadEnvelope` (the commands at their own path, views under `/views`). */
  async read<R>(
    projection: string,
    row: Reader<R>,
    params: ReadParams = {},
  ): Promise<ReadEnvelope<R> | null> {
    const query = new URLSearchParams({ generation: String(this.generationNumber) });
    if (params.accountId !== undefined && params.accountId !== null) {
      query.set("account_id", params.accountId);
    }
    query.set("page_rows", String(Math.min(params.pageRows ?? PAGE_ROWS_MAX, PAGE_ROWS_MAX)));
    if (params.cursor !== undefined) {
      query.set("cursor", params.cursor);
    }
    if (params.entityId !== undefined) {
      query.set("entity_id", params.entityId);
    }
    for (const [name, value] of Object.entries(params.filters ?? {})) {
      query.set(name, value);
    }
    const path = projection === "commands" ? COMMANDS_PATH : `${VIEWS_PATH}/${projection}`;
    return this.get(path, query, (json) => decodeEnvelope(json, row, projection));
  }
}
